import { RefetchQueriesInclude, useMutation, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import {
  CreateDocumentInput,
  ExtractionStatus,
  JobStatus,
  ProcessingStrategy,
} from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DOCUMENT_AI_ENRICHMENT_JOB_STATUS } from "components/Document/queries";

const QUEUE_DOCUMENT_AI_ENRICHMENT_JOB_MUTATION = gql(`
  mutation QueueDocumentAIEnrichmentJob($tenantId: ID!, $documents: [CreateDocumentInput!]!, $strategy: ProcessingStrategy!) {
    queueDocumentAIEnrichmentJob(input: { tenantId: $tenantId, documents: $documents, strategy: $strategy }) {
      id
      status
      documents {
        id
        title
        extractionStatus
      }
    }
  }
`);

type QueueDocumentAIEnrichmentJobResponse = {
  queueDocumentAIEnrichmentJob?: {
    id: string;
    status: string;
    documents: Array<{
      id: string;
      title: string;
      extractionStatus?: string | null;
    }>;
  };
};

type QueueDocumentAIEnrichmentJobVariables = {
  tenantId: string;
  documents: CreateDocumentInput[];
  strategy: ProcessingStrategy;
};

type DocumentAIEnrichmentJobResponse = {
  documentAIEnrichmentJob?: {
    id: string;
    status: JobStatus;
    documents: Array<{
      id: string;
      title: string;
      originalTitle?: string | null;
      extractionStatus?: ExtractionStatus | null;
      lastExtractionAt?: string | null;
    }>;
  };
};

type DocumentAIEnrichmentJobVariables = {
  id: string;
};

export interface UseAnalyzeDocumentsReturn {
  queueJob: (params: {
    tenantId: string;
    documents: CreateDocumentInput[];
    strategy: ProcessingStrategy;
    refetchQueries?: RefetchQueriesInclude;
  }) => Promise<string | undefined>;
  queueLoading: boolean;
  queueResponse:
    | QueueDocumentAIEnrichmentJobResponse["queueDocumentAIEnrichmentJob"]
    | null;
  job: DocumentAIEnrichmentJobResponse["documentAIEnrichmentJob"] | null;
  jobLoading: boolean;
  jobError: Error | undefined;
}

interface DocumentAIAnalysisContextType extends UseAnalyzeDocumentsReturn {
  setJobId: (jobId: string | null) => void;
}

const DocumentAIAnalysisContext = createContext<
  DocumentAIAnalysisContextType | undefined
>(undefined);

interface DocumentAIAnalysisProviderProps {
  children: ReactNode;
  queueConfig?: {
    refetchQueries?: RefetchQueriesInclude;
  };
}

export function DocumentAIAnalysisProvider({
  children,
  queueConfig,
}: DocumentAIAnalysisProviderProps) {
  const [jobId, setJobId] = useState<string | null>(null);

  const [
    queueDocumentAIEnrichmentJob,
    { loading: queueLoading, data: queueData, error: queueError },
  ] = useMutation<
    QueueDocumentAIEnrichmentJobResponse,
    QueueDocumentAIEnrichmentJobVariables
  >(QUEUE_DOCUMENT_AI_ENRICHMENT_JOB_MUTATION, {
    refetchQueries: queueConfig?.refetchQueries,
  });

  const {
    data: jobData,
    loading: jobLoading,
    error: jobError,
    stopPolling,
  } = useQuery<
    DocumentAIEnrichmentJobResponse,
    DocumentAIEnrichmentJobVariables
  >(DOCUMENT_AI_ENRICHMENT_JOB_STATUS, {
    variables: { id: jobId ?? "" },
    skip: !jobId,
    pollInterval: 1000,
    fetchPolicy: "network-only",
  });

  // Auto-start polling when jobId is set from queue response
  useEffect(() => {
    if (queueData?.queueDocumentAIEnrichmentJob?.id && !jobId) {
      setJobId(queueData.queueDocumentAIEnrichmentJob.id);
    }
  }, [queueData?.queueDocumentAIEnrichmentJob?.id, jobId]);

  // Stop polling when job reaches terminal status
  useEffect(() => {
    if (
      jobData?.documentAIEnrichmentJob?.status &&
      terminalJobStatuses.includes(jobData.documentAIEnrichmentJob.status)
    ) {
      stopPolling();
    }
  }, [jobData?.documentAIEnrichmentJob?.status, stopPolling]);

  const queueJob = useCallback(
    async (params: {
      tenantId: string;
      documents: CreateDocumentInput[];
      strategy: ProcessingStrategy;
      refetchQueries?: RefetchQueriesInclude;
    }): Promise<string | undefined> => {
      const { refetchQueries, ...variables } = params;
      const result = await queueDocumentAIEnrichmentJob({
        variables,
        refetchQueries: refetchQueries ?? queueConfig?.refetchQueries,
      });
      const newJobId = result.data?.queueDocumentAIEnrichmentJob?.id;
      if (newJobId) {
        setJobId(newJobId);
      }
      return newJobId;
    },
    [queueDocumentAIEnrichmentJob, queueConfig?.refetchQueries]
  );

  const queueResponse = useMemo(
    () => queueData?.queueDocumentAIEnrichmentJob ?? null,
    [queueData?.queueDocumentAIEnrichmentJob]
  );

  const job = useMemo(
    () => jobData?.documentAIEnrichmentJob ?? null,
    [jobData?.documentAIEnrichmentJob]
  );

  // Combine queue error and job error
  const combinedError = useMemo(() => {
    if (queueError) {
      return new Error(queueError.message || "Failed to queue job");
    }
    if (jobError) {
      return new Error(jobError.message || "Failed to fetch job status");
    }
    return undefined;
  }, [queueError, jobError]);

  const contextValue = useMemo(
    () => ({
      queueJob,
      queueLoading,
      queueResponse,
      job,
      jobLoading,
      jobError: combinedError,
      setJobId,
    }),
    [
      queueJob,
      queueLoading,
      queueResponse,
      job,
      jobLoading,
      combinedError,
      setJobId,
    ]
  );

  return (
    <DocumentAIAnalysisContext.Provider value={contextValue}>
      {children}
    </DocumentAIAnalysisContext.Provider>
  );
}

export function useAnalyzeDocuments(): UseAnalyzeDocumentsReturn {
  const context = useContext(DocumentAIAnalysisContext);

  if (context === undefined) {
    throw new Error(
      "useAnalyzeDocuments must be used within a DocumentAIAnalysisProvider"
    );
  }

  return {
    queueJob: context.queueJob,
    queueLoading: context.queueLoading,
    queueResponse: context.queueResponse,
    job: context.job,
    jobLoading: context.jobLoading,
    jobError: context.jobError,
  };
}
